"use client";

import React from "react";
import { gql } from "@apollo/client";
import { useRouter, useParams } from "next/navigation";
import {
	Breakpoint,
	Button,
	Hidden,
	IconDownload,
	Text,
} from "@jasperlabs/jux-next";
import { SomethingWentWrong } from "components/organisms";
import SomethingWasNotFound from "components/organisms/SomethingWasNotFound";
import LoadingPlaceholder from "components/organisms/LoadingPlaceholder";
import { OfferTags } from "components/organisms/offer/OfferTags";
import RenderMarkdown from "components/organisms/RenderMarkdown";
import RenderIfCondition from "components/organisms/RenderIfCondition";
import OfferHeroVideo from "components/organisms/offer/OfferHeroVideo";
import { useDoesScrollPositionSatisfy } from "components/utilities/useScrollPosition";
import { StickyFooter } from "components/organisms/StickyFooter";
import FormattedCurrencyText from "components/organisms/FormattedCurrencyText";
import { useSession } from "contexts/session";
import { LoginOrCreateAccountIfUnauthenticated } from "components/organisms/LoginLink";
import { InvestDesktop, InvestMobile } from "./invest";
import { OfferContentFragment, useOfferQuery } from "./page.generated";

const Divider = () => <hr className="border-muted my-6 md:my-8" />;

function OfferContent({ offer }: { offer: OfferContentFragment }) {
	const { hasAuthenticatedSession } = useSession();

	return (
		<>
			<div data-testid="offer-header">
				<div className="mb-6">
					<OfferTags offer={offer} />
				</div>

				<div className="mb-4 md:mb-6">
					<Text variant="heading-default">{offer.name}</Text>
				</div>

				<div className="mb-4 md:mb-8">
					<Text variant="body-xl">{offer.subtitle}</Text>
				</div>
				<LoginOrCreateAccountIfUnauthenticated>
					<Button
						as="a"
						href={offer.primaryDocument?.url}
						target="_blank"
						rel="noreferrer"
						iconLeft={<IconDownload />}
						className="w-full md:w-80"
						data-testid="primary-offer-document-download-button"
					>
						Offer Document
					</Button>
				</LoginOrCreateAccountIfUnauthenticated>
			</div>

			<Divider />

			<div data-testid="offer-overview">
				<div className="mb-4 md:mb-6">
					<Text variant="heading-xs">Overview</Text>
				</div>

				<div className="mb-6 rounded-md overflow-hidden">
					<OfferHeroVideo
						heroImageUrl={offer.cardImageUrl}
						videoUrl={offer.videoUrl}
					/>
				</div>

				<RenderIfCondition
					condition={Boolean(offer?.overview || offer.fund.blurb)}
				>
					<RenderMarkdown text={offer?.overview || offer.fund.blurb} />
				</RenderIfCondition>
			</div>

			<RenderIfCondition
				condition={!!offer.documents && offer.documents.length > 0}
			>
				<Divider />

				<div data-testid="offer-documents">
					<div className="mb-4 md:mb-6">
						<Text variant="heading-xs">Documents</Text>
					</div>

					<div className="border border-muted rounded-md divide-y divide-muted">
						{offer.documents!.map((document) => (
							<LoginOrCreateAccountIfUnauthenticated
								key={document.id}
								action="login"
							>
								<a
									href={hasAuthenticatedSession ? document.file.url : "#"}
									target="_blank"
									rel="noreferrer"
									className="p-4 flex items-center hover:bg-neutral-50 transition-colors"
									data-testid={`offer-document-${document.id}`}
								>
									<Text variant="body-lg" className="truncate">
										{document.title}
									</Text>
									<Text variant="body-sm" className="ml-auto flex items-center">
										<IconDownload className="mr-1" fontSize={18} /> View
									</Text>
								</a>
							</LoginOrCreateAccountIfUnauthenticated>
						))}
					</div>
				</div>
			</RenderIfCondition>

			<RenderIfCondition
				condition={!!offer.dataBlocks && offer.dataBlocks.length > 0}
			>
				{offer.dataBlocks!.map((block) => (
					<>
						<Divider />

						<div data-testid={`offer-data-block-${block.id}`}>
							<div className="mb-4 md:mb-6">
								<Text variant="heading-xs">{block.title}</Text>
							</div>

							{block.__typename === "OfferTextBlock" && (
								<RenderMarkdown text={block.content} />
							)}

							{block.__typename === "OfferTableBlock" && (
								<div className="border border-muted rounded-md divide-y divide-muted">
									{block.rows.map((row) => (
										<div key={row.name} className="p-4 flex items-center">
											<Text variant="body-lg" className="truncate">
												{row.name}
											</Text>
											<Text
												variant="heading-xs"
												className="!text-[18px] ml-auto"
											>
												{row.value}
											</Text>
										</div>
									))}
								</div>
							)}
						</div>
					</>
				))}
			</RenderIfCondition>
		</>
	);
}

function usePageQuery() {
	const params = useParams<{ slug: string }>();
	const { hasAuthenticatedSession } = useSession();

	return useOfferQuery({
		variables: { slug: params!.slug, skipSession: !hasAuthenticatedSession },
	});
}

export default function Offer() {
	const { data, error, loading } = usePageQuery();

	const router = useRouter();

	const isMobileInvestFooterVisible = useDoesScrollPositionSatisfy(
		(position: number) => position > 420,
	);

	if (loading) {
		return <LoadingPlaceholder />;
	}

	if (error) {
		return (
			<div className="mt-20">
				<SomethingWentWrong />
			</div>
		);
	}

	if (!data?.offer) {
		return <SomethingWasNotFound />;
	}

	const { offer, session } = data;

	if (
		offer.status === "CLOSED" ||
		(offer.hideOffer && !session?.impersonatedBy?.id)
	) {
		router.replace("/invest");
		return null;
	}

	return (
		<>
			<div className="max-w-container-lg container mx-auto px-5 py-6 md:py-8">
				<div className="grid grid-cols-12 gap-x-6">
					<div className="col-span-12 lg:col-span-8">
						<OfferContent offer={offer} />
					</div>
					<div className="col-span-12 lg:col-span-4">
						<div className="sticky top-[108px]">
							<Hidden to={Breakpoint.LG}>
								<InvestDesktop offer={offer} />
							</Hidden>
						</div>
					</div>
				</div>
			</div>
			<Hidden from={Breakpoint.LG}>
				<StickyFooter isVisible={isMobileInvestFooterVisible}>
					<div className="flex justify-center border-t border-solid border-neutral-200 bg-white p-4">
						<InvestMobile offer={offer} />
					</div>
				</StickyFooter>
			</Hidden>
		</>
	);
}

gql`
	${OfferTags.fragment}
	${FormattedCurrencyText.fragment}
	fragment OfferContent on Offer {
		id
		...OfferTags
		name
		overview
		subtitle
		cardImageUrl
		videoUrl
		fund {
			id
			blurb
		}
		primaryDocument {
			id
			url
		}
		documents {
			id
			title
			file {
				id
				url
			}
		}
		dataBlocks {
			id
			title
			... on OfferTextBlock {
				content
			}
			... on OfferTableBlock {
				rows {
					name
					value
				}
			}
		}
	}

	fragment OfferInvest on Offer {
		id
		digitalSubscription
		pricePerUnit
		estimatedPricePerUnit
		totalUnitCountDecimal {
			value
			decimalPlaces
		}
		minUnitsPerInvestmentDecimal {
			value
			decimalPlaces
		}
		maxUnitsPerInvestmentDecimal {
			value
			decimalPlaces
		}
		fundsDeadline
		hideOffer
		investor {
			allocationRequests {
				id
				status
				created
				unitCountDecimal {
					value
					decimalPlaces
				}
				investingEntity {
					id
				}
			}
			hasRegisteredInterest
			outstandingRequirementsToInvest
		}

		fund {
			id
			name
			unitPrecisionScale
			currency {
				id
				...FormattedCurrencyTextCurrencyFields
			}
		}
	}

	query Offer($slug: ID!, $skipSession: Boolean! = true) {
		offer(slug: $slug) {
			id
			slug
			...OfferContent
			...OfferInvest
		}
		session @skip(if: $skipSession) {
			impersonatedBy {
				id
			}
		}
	}
`;
